// @ts-strict
import {
  ChangeDetectionStrategy,
  Component,
  OnInit,
  ViewChild,
  ChangeDetectorRef,
  NgZone,
  Input,
  Output,
  EventEmitter,
  AfterViewInit,
  OnDestroy,
  ViewContainerRef,
} from "@angular/core";
import {
  LMActionPickerService,
  LMFieldPickerService,
} from "@app/components/modal";
import { SohoAlertDirective, SohoDataGridComponent } from "ids-enterprise-ng";
import { Observable, Subject, takeUntil } from "rxjs";
import { LMConfigurationService } from "@app/services";
import { LMFieldPickerSelectOptions } from "@app/components/modal/field-picker/lm-field-picker";

@Component({
  standalone: false,
  selector: "lm-list-personalization-toolbar",
  templateUrl: "./lm-list-personalization-toolbar.component.html",
  styleUrl: "./lm-list-personalization-toolbar.component.scss",
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [ LMActionPickerService, LMFieldPickerService, LMConfigurationService ]
})
export class LMListPersonalizationToolbarComponent
  implements OnInit, AfterViewInit, OnDestroy {
  @ViewChild("actionDataGridComponent", { static: false })
  private dg!: SohoDataGridComponent;
  @ViewChild(SohoAlertDirective) private conditionAlert!: SohoAlertDirective;

  @Input() listUrlInfo!: LMListUrlInfo;
  @Input() businessClass!: string;
  @Input() listName!: string;
  @Input() excludedActions: string[] = []; // array of restricted action names
  @Input() calledOutToolbarActions: LMReportPersonalizationCalledOutAction[] = [];
  @Output() calledOutToolbarActionsChange = new EventEmitter<LMReportPersonalizationCalledOutAction[]>();

  private readonly destroyer$ = new Subject<void>();
  private actionPickerResult$ = new Observable<LMActionSpec[]>();

  public actionGridOptions!: SohoDataGridOptions;
  public selectedAction: LMReportPersonalizationCalledOutAction | null = null;

  private conditionAlertId = 'list-personalization-toolbar-conditions-alert';

  constructor(
    private readonly cdr: ChangeDetectorRef,
    private readonly ngZone: NgZone,
    private readonly actionPickerService: LMActionPickerService,
    private readonly lmConfigurationService: LMConfigurationService,
    private readonly lmFieldPickerService: LMFieldPickerService
  ) {}

  ngOnInit(): void {
    this.initializeGrid();
  }

  ngAfterViewInit(): void {
    if (this.dg) {
      this.dg.dataset = this.calledOutToolbarActions || [];
      if (this.calledOutToolbarActions.length > 0) {
        this.setSelectedRowData(0);
      }
    }
  }

  ngOnDestroy(): void {
    this.destroyer$.next();
    this.destroyer$.complete();
  }

  get selectedRow(): SohoDataGridSelectedRow | null {
    return this.dg?.selectedRows()?.[0] || null;
  }

  get selectedRowIndex(): number {
    return this.selectedRow?.idx ?? -1;
  }

  get isReadOnly(): boolean {
    const { type } = this.selectedAction;
    return ![ "action", "standard", "navigation" ].includes(type);
  }

  /** Toolbar Events */
  onAddAction(): void {
    this.openActionPickerByType([ "action", "standard" ]);
  }

  onAddNavigation(): void {
    this.openActionPickerByType([ "navigation" ]);
  }

  onRemoveAction(): void {
    const currentIndex = this.selectedRowIndex;
    if (currentIndex === -1) return;

    this.calledOutToolbarActions = this.calledOutToolbarActions.filter(
      (action, index) => index !== currentIndex
    );
    this.dg.dataset = this.calledOutToolbarActions;
    this.selectedAction = null;

    if (this.calledOutToolbarActions.length > 0) {
      const newIndex = Math.min(
        currentIndex,
        this.calledOutToolbarActions.length - 1
      );
      this.setSelectedRowData(newIndex);
    }
    this.modelChanged();
    this.cdr.detectChanges();
  }

  /** Datagrid Events */
  onRowClicked(event: SohoDataGridRowClicked): void {
    if (!event) return;
    if (event.row > -1) {
      this.setSelectedRowData(event.row);
      // need to revalidate condition field of the selected row?
      this.validateCondition();
      this.conditionAlert.removeAllMessages();
    }
  }

  onRowReordered(event: SohoDataGridRowClicked): void {
    if (!event) return;
    this.calledOutToolbarActions = [
      ...(this.dg.dataset as LMReportPersonalizationCalledOutAction[]),
    ];
  }

  /** Action Detail Events */
  onDetailChange() {
    if (this.selectedAction) {
      this.updateGrid();
    }
  }

  onGetConditionQualifier = () => {
    const fieldLookUpUrl = this.listUrlInfo?.fieldLookUpUrl || "";

    const options: LMFieldPickerSelectOptions = {
      view: "SimpleConditions",
      businessClass: this.businessClass,
    };
    this.lmFieldPickerService
      .openFieldPickerDialog(options, fieldLookUpUrl, <ViewContainerRef>{})
      .subscribe((result) => {
        if (result?.fieldName && this.selectedAction) {
          this.selectedAction.condition = result.fieldName;
          this.onDetailChange();
        }
      });
  };

  public validateCondition(): void {
    if (this.selectedAction?.condition === "") {
      this.updateConditionAlert();
    } else {
      if (this.selectedAction?.condition)
        this.lmConfigurationService
          .conditionIsValid(
            this.selectedAction.condition,
            this.listUrlInfo.reportPersonalizationUrl || "",
            this.businessClass
          )
          .pipe(takeUntil(this.destroyer$))
          .subscribe((response: LMLplValidationResponse) => {
            this.updateConditionAlert(response);
          });
    }
  }

  /** Private Methods */
  private updateConditionAlert(
    lplValidationResponse?: LMLplValidationResponse
  ): void {
    if (
      !lplValidationResponse ||
      lplValidationResponse.lplStatus === "LPL_VALID"
    ) {
      this.conditionAlert.removeAllMessages();
    } else {
      const validationError = lplValidationResponse.validationError;
      let alertMessage = lplValidationResponse.message || "";

      if (validationError) {
        alertMessage = validationError.errorDescription;
      }
      const alertType =
        validationError && validationError.isWarning ? "info" : "error";
      this.conditionAlert.addInlineMessage(alertMessage, alertType, true, undefined, undefined, this.conditionAlertId);
    }
  }

  private openActionPickerByType(type?: ReadonlyArray<LMActionPickerActionType>): void {
    // exclude actions that are already in the list
    let excludeActions = this.calledOutToolbarActions?.map(
      (action) => action.name
    );

    if (Array.isArray(this.excludedActions)) {
      excludeActions = [ ...excludeActions, ...this.excludedActions ];
    }

    this.actionPickerService.openActionPickerDialog(
      this.businessClass,
      this.listName,
      type,
      excludeActions
    )
    .pipe(takeUntil(this.destroyer$))
    .subscribe(this.handleAddActions);
  }

  private setSelectedRowData(row: number): void {
    const selectedAction = this.calledOutToolbarActions[row];

    if (selectedAction) {
      this.selectedAction = selectedAction;
      this.dg.selectRows(row);
      this.cdr.detectChanges();
    }
  }

  private updateGrid(
    newActions?: LMReportPersonalizationCalledOutAction[]
  ): void {
    this.ngZone.runOutsideAngular(() => {
      if (!this.dg?.dataset) return;

      if (!newActions) {
        const selectedRowIdx = this.selectedRowIndex;

        if (selectedRowIdx === undefined || selectedRowIdx === -1) return;

        this.dg.rowStatus(
          selectedRowIdx,
          "new",
          Soho.Locale.translate("RowDataChanged")
        );
        this.dg.dataset = this.calledOutToolbarActions;
        this.setSelectedRowData(selectedRowIdx);
      } else {
        this.calledOutToolbarActions = [
          ...this.calledOutToolbarActions,
          ...newActions,
        ];

        this.dg.dataset = this.calledOutToolbarActions;

        const toolbarActionsLen = this.calledOutToolbarActions.length;
        const startIndex = toolbarActionsLen - newActions.length;

        for (let i = startIndex; i < toolbarActionsLen; i++) {
          this.dg.rowStatus(i, "new", Soho.Locale.translate("RowDataAdded"));
        }

        const lastIndex = toolbarActionsLen - 1;

        if (lastIndex >= 0) {
          this.setSelectedRowData(lastIndex);
        }
      }

      this.ngZone.run(() => {
        this.modelChanged();
        this.cdr.detectChanges();
      });
    });
  }

  private modelChanged(): void {
    this.calledOutToolbarActionsChange.emit(this.calledOutToolbarActions);
  }

  private handleAddActions = (actionSpecs: LMActionSpec[] | null): void => {
    if (!Array.isArray(actionSpecs) || actionSpecs.length === 0) return;

    const newActions: LMReportPersonalizationCalledOutAction[] = actionSpecs.map(
      (action) => ({
        type: action.type || "action",
        actionSpecType: action.actionSpecType,
        name: action?.name || "",
        label: action.label || ""
      })
    );

    this.updateGrid(newActions);
  };

  private getGenericActionName(type: string): string {
    let genericActionName!: string;

    switch (type) {
      case "action":
      case "standard":
        genericActionName = "Action";
        break;
      case "navigation":
        genericActionName = "Navigation";
        break;
      case "forminvoke":
        genericActionName = "Form Invoke";
        break;
      case "httpurl":
        genericActionName = "Link";
        break;
      case "subform":
        genericActionName = "Sub Form";
        break;
      default:
        genericActionName = "Unknown";
        break;
    }

    return genericActionName;
  }

  private initializeGrid(): void {
    const customFormatter: SohoDataGridColumnFormatterFunction = (
      row: number,
      cell: unknown,
      value: unknown,
      col: SohoDataGridColumn,
      item: LMReportPersonalizationCalledOutAction | any,
      api: SohoDataGridStatic
    ) => {
      let content = "Unknown";

      if (col.field === "actionSpecType") {
        if (item.type) {
          content = this.getGenericActionName(item.type);
        }
      }

      return Soho.Formatters.Input(row, cell, content, col, item, api);
    };

    this.actionGridOptions = {
      selectable: "single",
      paging: false,
      rowReorder: true,
      useLocaleSort: false,
      editable: false,
      rowHeight: "short",
      isList: true,
      showDirty: true,
      enableTooltips: true,
      headerBackgroundColor: "light",
      disableRowDeactivation: true,
      disableRowDeselection: true,
      columns: [
        {
          id: "rowReorder",
          name: "",
          field: "id",
          align: "center",
          sortable: false,
          formatter: Soho.Formatters.RowReorder,
          width: 10,
          resizable: false,
        },
        {
          id: "label",
          name: Soho.Locale.translate("ListLabel"),
          sortable: true,
          field: "label",
          formatter: Soho.Formatters.Text,
          textOverflow: "ellipsis",
        },
        {
          id: "type",
          name: Soho.Locale.translate("Type"),
          sortable: true,
          field: "actionSpecType",
          formatter: customFormatter,
          textOverflow: "ellipsis",
        },
      ],
      dataset: this.calledOutToolbarActions,
      emptyMessage: {
        title: Soho.Locale.translate("Toolbar_DataGridEmptyTitle"),
        info: Soho.Locale.translate("Toolbar_DataGridEmptyMessage"),
      },
    };
  }
}
